import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeft, Edit, Building2, Phone, MapPin, CreditCard, Percent, Calendar, DollarSign, FileText, Download, X, Upload, Trash, ZoomIn, ZoomOut, RotateCw, RotateCcw, RefreshCcw, Printer } from 'lucide-react';
import { formatIndianCurrency } from '../utils/numberToWords';
import { fetchInvoices, fetchInvoiceRules } from '../utils/api';
import InvoicePreview from '../components/InvoicePreview';
import AdhocInvoicePreview from '../components/AdhocInvoicePreview';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import { getEscalationInfo } from '../utils/rentEscalation';

function ClientDetails({ clients, config, facilities, currentUser, onUpdateClient, onRemoveClient }) {
  // Helpers for DD/MM/YYYY display and ISO serialization
  const toDisplayDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const toISODate = (display) => {
    if (!display) return '';
    const m = String(display).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return display;
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  };

  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState({});

  const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5050';
  const getFileName = (p) => {
    try { return (p || '').split('/').pop(); } catch { return ''; }
  };
  const [viewerDoc, setViewerDoc] = useState(null); // { label, url }
  const openViewer = (label, path) => {
    if (!path) return;
    const company = (client?.registeredName && client.registeredName.trim()) ? client.registeredName : client?.name;
    const title = company ? `${company} - ${label}` : label;
    setViewerDoc({ label: title, url: `${API_BASE}${path}` });
  };
  const closeViewer = () => { setViewerDoc(null); setImageScale(1); setImageRotate(0); };
  const isImageUrl = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some(ext => lower.endsWith(ext));
  };
  const [imageScale, setImageScale] = useState(1);
  const [imageRotate, setImageRotate] = useState(0);

  // Document upload support in edit modal
  const DOC_FIELDS = [
    { key: 'aadhar', label: 'Aadhar' },
    { key: 'pan', label: 'PAN' },
    { key: 'companyIncorporationCertificate', label: 'Company Incorporation Certificate' },
    { key: 'gstCertificate', label: 'GST Certificate' },
    { key: 'gstrFiling', label: 'GSTR Filing' },
    { key: 'leaseDeed', label: 'Lease Deed' },
    { key: 'loi', label: 'LOI' },
    { key: 'termsheet', label: 'Term Sheet' },
    { key: 'serviceAgreement', label: 'Service Agreement' },
  ];
  const [docFiles, setDocFiles] = useState({});
  const setDocFile = (key, file) => setDocFiles(prev => ({ ...prev, [key]: file }));
  const clearDocFile = (key) => setDocFiles(prev => ({ ...prev, [key]: undefined }));
  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024)) || 0;
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  // Tabs
  const [activeTab, setActiveTab] = useState('company'); // 'company' | 'documents' | 'invoices' | 'ledger'

  // Invoice rules for auto-assigning bank account
  const [invoiceRules, setInvoiceRules] = useState([]);

  // Load invoice rules on mount
  useEffect(() => {
    const loadInvoiceRules = async () => {
      try {
        const rules = await fetchInvoiceRules();
        setInvoiceRules(Array.isArray(rules) ? rules : []);
      } catch (error) {
        console.error('Failed to load invoice rules:', error);
        setInvoiceRules([]);
      }
    };
    loadInvoiceRules();
  }, []);

  // Auto-assign bank account when facility changes
  useEffect(() => {
    if (formData.facilityId && invoiceRules.length > 0) {
      // Find first rule matching the selected facility
      const matchingRule = invoiceRules.find(
        rule => String(rule.facilityId) === String(formData.facilityId)
      );
      if (matchingRule) {
        setFormData(prev => ({
          ...prev,
          bankAccountId: matchingRule.bankAccountId
        }));
      } else {
        // Clear bank account if no rule found
        setFormData(prev => ({
          ...prev,
          bankAccountId: ''
        }));
      }
    } else if (!formData.facilityId) {
      // Clear bank account if facility is cleared
      setFormData(prev => ({
        ...prev,
        bankAccountId: ''
      }));
    }
  }, [formData.facilityId, invoiceRules]);

  // Invoices for this client
  const [clientInvoices, setClientInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  useEffect(() => {
    if (!client) return;
    setLoadingInvoices(true);
    fetchInvoices()
      .then(list => {
        const filtered = (list || []).filter(inv => {
          const invClient = inv.client && (inv.client._id || inv.client);
          const id = client._id || client.id;
          return String(invClient) === String(id);
        }).sort((a, b) => {
          const ad = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
          const bd = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
          return bd - ad; // latest first
        });
        setClientInvoices(filtered);
      })
      .catch(() => setClientInvoices([]))
      .finally(() => setLoadingInvoices(false));
  }, [client]);

  // Invoice preview
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const exportLedgerExcel = () => {
    const rows = [];
    let balance = 0;
    clientInvoices.forEach(inv => {
      const date = inv.invoiceDate ? new Date(inv.invoiceDate) : null;
      const total = Number(inv.totals?.total || 0);
      balance += total;
      rows.push({
        Date: date ? XLSX.SSF.format('dd/mm/yyyy', date) : '',
        Description: `Invoice ${inv.invoiceType === 'credit_note' 
          ? (inv.creditNoteNumber || inv.invoiceNumber)
          : formatInvoiceNumber(inv.invoiceNumber, inv.financialYear, config)
        }`,
        Debit: total,
        Credit: 0,
        Balance: balance
      });
      const received = Number(inv.receivedAmount || 0);
      if (received > 0) {
        const payDate = inv.receivedDate ? new Date(inv.receivedDate) : date;
        balance -= received;
        rows.push({
          Date: payDate ? XLSX.SSF.format('dd/mm/yyyy', payDate) : '',
          Description: `Payment ${inv.utrNumber || ''}`.trim(),
          Debit: 0,
          Credit: received,
          Balance: balance
        });
      }
    });

    if (rows.length === 0) {
      alert('No transactions to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    const fileName = `${(client?.registeredName || client?.name || 'client').toString().replace(/[^a-z0-9]+/gi,'-').toLowerCase()}-ledger.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    const foundClient = clients.find(c => c._id === clientId || c.id === clientId);
    if (foundClient) {
      setClient(foundClient);
      setFormData({
        name: foundClient.name,
        registeredName: foundClient.registeredName || '',
        billToAddress: foundClient.billToAddress,
        shipToAddress: foundClient.shipToAddress,
        phone: foundClient.phone,
        gstin: foundClient.gstin,
        bankAccountId: foundClient.bankAccountId || '',
        rentAmount: foundClient.rentAmount || '',
        securityDeposit: foundClient.securityDeposit || '',
        escalationPercentage: foundClient.escalationPercentage || '',
        rentStartDate: toDisplayDate(foundClient.rentStartDate),
        facilityId: foundClient.facilityId || ''
      });
    }
  }, [clientId, clients]);

  // Set page title to company name
  useEffect(() => {
    if (client?.name) {
      document.title = client.name;
    }
    return () => {
      // Optional: restore default title when leaving
      // document.title = 'Clients';
    };
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === 'rentStartDate') {
          fd.append(k, toISODate(v));
        } else {
          fd.append(k, v);
        }
      });
      DOC_FIELDS.forEach(({ key }) => { const f = docFiles[key]; if (f) fd.append(key, f); });
      await onUpdateClient(client._id || client.id, fd);
      setShowEditForm(false);
      // Refresh client data
      const updatedClient = clients.find(c => c._id === clientId || c.id === clientId);
      setClient(updatedClient);
    } catch (err) {
      alert('Failed to update client');
    }
  };

  const handleCancel = () => {
    setShowEditForm(false);
    setFormData({
      name: client.name,
      registeredName: client.registeredName || '',
      billToAddress: client.billToAddress,
      shipToAddress: client.shipToAddress,
      phone: client.phone,
      gstin: client.gstin,
      bankAccountId: client.bankAccountId || '',
      rentAmount: client.rentAmount || '',
      securityDeposit: client.securityDeposit || '',
      escalationPercentage: client.escalationPercentage || '',
      rentStartDate: toDisplayDate(client.rentStartDate),
      facilityId: client.facilityId || ''
    });
  };

  if (!client) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Client not found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/clients')}>
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const selectedBank = config.bankAccounts && config.bankAccounts.find(bank => bank.id === client.bankAccountId);
  const facilityName = facilities && facilities.find(fac => String(fac.id) === String(client.facilityId))?.name;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button 
            className="btn" 
            onClick={() => navigate('/clients')}
            aria-label="Back"
            title="Back"
            style={{
              marginRight: 16,
              width: 56,
              height: 56,
              borderRadius: 16,
              background: '#e9eff7',
              border: '1px solid #e0e7ff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#111827',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h1>{client.name}</h1>
            <p>Client Details</p>
          </div>
        </div>
        {!showEditForm && (
          <div className="page-actions">
            {currentUser?.role === 'admin' && (
              <button className="btn btn-primary" onClick={() => setShowEditForm(true)}>
                <Edit size="1em" style={{marginRight: 6}}/>Edit Client
              </button>
            )}
            {currentUser?.role === 'admin' && (
              <button className="btn btn-danger" onClick={async () => {
                // Check if client has invoices
                if (clientInvoices.length > 0) {
                  const hasInvoices = window.confirm(
                    `This client has ${clientInvoices.length} invoice(s). Deleting the client will also delete all associated invoices. Are you sure you want to proceed?`
                  );
                  if (!hasInvoices) return;
                } else {
                  const confirmed = window.confirm('Are you sure you want to delete this client? This action cannot be undone.');
                  if (!confirmed) return;
                }
                
                try {
                  await onRemoveClient(client._id || client.id);
                  navigate('/clients');
                } catch (error) {
                  alert('Failed to delete client. Please try again.');
                }
              }}>
                <Trash size="1em" style={{marginRight: 6}}/>Delete
              </button>
            )}
          </div>
        )}
      </div>

      {showEditForm ? (
        <div className="modal-right-overlay">
          <div className="modal-right-content modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <h2>Edit Client</h2>
            </div>
            <div className="modal-right-body">
              <form id="client-form" onSubmit={handleSubmit} encType="multipart/form-data">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0px' }}>
                  <div className="form-group">
                    <label>Client Name</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Registered Name</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formData.registeredName}
                      onChange={(e) => setFormData({...formData, registeredName: e.target.value})}
                      placeholder="Enter registered company name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bill To Address</label>
                    <textarea
                      className="form-input"
                      value={formData.billToAddress}
                      onChange={(e) => setFormData({...formData, billToAddress: e.target.value})}
                      rows="3"
                      required
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Ship To Address</label>
                    <textarea
                      className="form-input"
                      value={formData.shipToAddress}
                      onChange={(e) => setFormData({...formData, shipToAddress: e.target.value})}
                      rows="3"
                      placeholder="Leave blank if same as bill to address"
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      className="form-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formData.gstin}
                      onChange={(e) => setFormData({...formData, gstin: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Facility</label>
                    <Select
                      style={{ width: '100%' }}
                      value={formData.facilityId || undefined}
                      onChange={(v) => setFormData({ ...formData, facilityId: v })}
                      placeholder="Select a facility"
                      options={(facilities || []).map(fac => {
                        const facilityName = fac.name || fac.facilityName || `Facility ${fac.id}`;
                        return { label: facilityName, value: fac.id };
                      })}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </div>
                  <div className="form-group">
                    <label>Rent Amount</label>
                    <input
                      className="form-input"
                      type="number"
                      value={formData.rentAmount}
                      onChange={(e) => setFormData({...formData, rentAmount: e.target.value})}
                      placeholder="Enter monthly rent amount"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Security Deposit</label>
                    <input
                      className="form-input"
                      type="number"
                      value={formData.securityDeposit}
                      onChange={(e) => setFormData({...formData, securityDeposit: e.target.value})}
                      placeholder="Enter refundable deposit amount"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Escalation Percentage</label>
                    <input
                      className="form-input"
                      type="number"
                      value={formData.escalationPercentage}
                      onChange={(e) => setFormData({...formData, escalationPercentage: e.target.value})}
                      placeholder="Enter escalation %"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Rent Start Date</label>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={formData.rentStartDate ? dayjs(toISODate(formData.rentStartDate)) : null}
                      onChange={(d) => setFormData({ ...formData, rentStartDate: d ? d.format('DD/MM/YYYY') : '' })}
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                      allowClear
                    />
                  </div>
                </div>
                <div className="config-section card-style" style={{ marginTop: '12px' }}>
                  <div className="config-section-header">
                    <h3>Client Documents</h3>
                  </div>
                  <div className="upload-grid">
                    {DOC_FIELDS.map(({ key, label }) => {
                      const existingPath = client?.documents?.[key];
                      return (
                      <div className="upload-item" key={key}>
                        <div className="upload-title">{label}</div>
                        {!docFiles[key] && existingPath ? (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px', border: '1px solid #e6ecf1', borderRadius: 10, background: '#fff', width: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ width: 36, height: 36, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FileText size={18} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{getFileName(existingPath)}</div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>Existing document</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <a href={`${API_BASE}${existingPath}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: 12 }} title="Open">
                                <Download size={14} />
                              </a>
                              <label htmlFor={`edit-file-${key}`} className="btn btn-primary" style={{ padding: '6px 8px', fontSize: 12, cursor: 'pointer', color:'#fff' }}>
                                Replace
                                <input id={`edit-file-${key}`} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => setDocFile(key, e.target.files?.[0])} />
                              </label>
                            </div>
                          </div>
                        ) : !docFiles[key] ? (
                          <label htmlFor={`edit-file-${key}`} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: '2px dashed #e1e5ee',
                            borderRadius: 10, cursor: 'pointer', color: '#555', background: '#fafafa', width: '100%'
                          }}>
                            <Upload size={18} />
                            <span>Drop items here or <strong>Browse files</strong></span>
                            <input
                              id={`edit-file-${key}`}
                              type="file"
                              accept="image/*,application/pdf"
                              style={{ display: 'none' }}
                              onChange={e => setDocFile(key, e.target.files?.[0])}
                            />
                          </label>
                        ) : (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px', border: '1px solid #e6ecf1', borderRadius: 10, background: '#fff', width: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ width: 36, height: 36, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FileText size={18} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{docFiles[key]?.name}</div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>{formatBytes(docFiles[key]?.size)}</div>
                              </div>
                            </div>
                            <button type="button" className="btn btn-danger" onClick={() => clearDocFile(key)} title="Remove">
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" form="client-form" className="btn btn-primary">
                  Update Client
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="content-section">
          {/* Tabs */}
          <div className="invoice-type-tabs" style={{ marginTop: 0 }}>
            <button onClick={() => setActiveTab('company')} className={`invoice-type-tab ${activeTab === 'company' ? 'active' : ''}`}>Company Information</button>
            <button onClick={() => setActiveTab('documents')} className={`invoice-type-tab ${activeTab === 'documents' ? 'active' : ''}`}>Documents</button>
            <button onClick={() => setActiveTab('invoices')} className={`invoice-type-tab ${activeTab === 'invoices' ? 'active' : ''}`}>Invoices</button>
            <button onClick={() => setActiveTab('ledger')} className={`invoice-type-tab ${activeTab === 'ledger' ? 'active' : ''}`}>Ledger</button>
          </div>

          {activeTab === 'company' && (
          <>
          {/* Summary tiles inspired by dashboard stats */}
          <div className="stats-grid cols-3" style={{ marginBottom: 16 }}>
            <div className="stat-card" style={{ alignItems: 'center' }}>
              <div className="stat-icon"><Building2 size="1.6em" color="#43a047"/></div>
              <div className="stat-content">
                <h3>Company</h3>
                <p className="stat-value" style={{ fontSize: '1.6rem' }}>{client.registeredName || client.name}</p>
              </div>
            </div>
            <div className="stat-card" style={{ alignItems: 'center' }}>
              <div className="stat-icon"><FileText size="1.6em" color="#e53935"/></div>
              <div className="stat-content">
                <h3>GSTIN</h3>
                <p className="stat-value" style={{ fontSize: '1.6rem' }}>{client.gstin || 'N/A'}</p>
              </div>
            </div>
            <div className="stat-card" style={{ alignItems: 'center' }}>
              <div className="stat-icon"><MapPin size="1.6em" color="#43a047"/></div>
              <div className="stat-content">
                <h3>Facility</h3>
                <p className="stat-value" style={{ fontSize: '1.6rem' }}>{facilityName || 'Not selected'}</p>
              </div>
            </div>
          </div>
          {/* Basic Information Section */}
          <div className="info-section">
            <div className="info-section-header">
              <Building2 size="1.2em" />
              <h3>Basic Information</h3>
            </div>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-label">Company Name</div>
                <div className="info-card-value">{client.name}</div>
              </div>
              {client.registeredName && (
                <div className="info-card">
                  <div className="info-card-label">Registered Name</div>
                  <div className="info-card-value">{client.registeredName}</div>
                </div>
              )}
              <div className="info-card">
                <div className="info-card-label">GSTIN</div>
                <div className="info-card-value">{client.gstin}</div>
              </div>
              <div className="info-card">
                <div className="info-card-label">Phone</div>
                <div className="info-card-value">{client.phone}</div>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="info-section">
            <div className="info-section-header">
              <MapPin size="1.2em" />
              <h3>Address Information</h3>
            </div>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-label">Bill To Address</div>
                <div className="info-card-value" style={{ whiteSpace: 'pre-line' }}>{client.billToAddress}</div>
              </div>
              {client.shipToAddress && (
                <div className="info-card">
                  <div className="info-card-label">Ship To Address</div>
                  <div className="info-card-value" style={{ whiteSpace: 'pre-line' }}>{client.shipToAddress}</div>
                </div>
              )}
            </div>
          </div>

          {/* Financial Information Section */}
          <div className="info-section">
            <div className="info-section-header">
              <DollarSign size="1.2em" />
              <h3>Financial Information</h3>
            </div>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-label">Bank Account</div>
                <div className="info-card-value">
                  {selectedBank 
                    ? (selectedBank.customTitle && selectedBank.customTitle.trim() !== '' 
                        ? selectedBank.customTitle 
                        : `${selectedBank.bankName} - ${selectedBank.accountName}`)
                    : 'Not selected'}
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-label">Facility</div>
                <div className="info-card-value">{facilityName || 'Not selected'}</div>
              </div>
              {client.rentAmount && (
                <div className="info-card">
                  <div className="info-card-label">Rent Amount</div>
                  <div className="info-card-value">{formatIndianCurrency(Number(client.rentAmount), 0)}</div>
                </div>
              )}
              {client.securityDeposit && (
                <div className="info-card">
                  <div className="info-card-label">Security Deposit</div>
                  <div className="info-card-value">{formatIndianCurrency(Number(client.securityDeposit), 0)}</div>
                </div>
              )}
              {client.escalationPercentage && (
                <div className="info-card">
                  <div className="info-card-label">Escalation %</div>
                  <div className="info-card-value">{client.escalationPercentage}%</div>
                </div>
              )}
              {client.rentStartDate && (
                <div className="info-card">
                  <div className="info-card-label">Rent Start Date</div>
                  <div className="info-card-value">{format(new Date(client.rentStartDate), 'dd/MM/yyyy')}</div>
                </div>
              )}
              {client.rentAmount && client.escalationPercentage && client.rentStartDate && (() => {
                const escalationInfo = getEscalationInfo(
                  client.rentAmount,
                  client.escalationPercentage,
                  client.rentStartDate,
                  new Date()
                );
                
                if (escalationInfo.hasEscalation) {
                  return (
                    <div className="info-card" style={{border: '2px solid #2563eb', backgroundColor: '#f0f9ff'}}>
                      <div className="info-card-label">Current Escalated Rent</div>
                      <div className="info-card-value" style={{color: '#2563eb', fontWeight: 'bold'}}>
                        {formatIndianCurrency(escalationInfo.escalatedAmount, 0)}
                        <div style={{fontSize: '0.8em', color: '#666', fontWeight: 'normal'}}>
                          (+{formatIndianCurrency(escalationInfo.escalationAmount, 0)} after {escalationInfo.yearsElapsed} year{escalationInfo.yearsElapsed > 1 ? 's' : ''})
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Show current rent even when no escalation has occurred yet
                  const rentStartDate = new Date(client.rentStartDate);
                  const currentDate = new Date();
                  const yearsUntilEscalation = rentStartDate.getFullYear() - currentDate.getFullYear();
                  
                  if (yearsUntilEscalation > 0) {
                    // Future escalation
                    return (
                      <div className="info-card" style={{border: '2px solid #10b981', backgroundColor: '#f0fdf4'}}>
                        <div className="info-card-label">Current Rent</div>
                        <div className="info-card-value" style={{color: '#10b981', fontWeight: 'bold'}}>
                          {formatIndianCurrency(client.rentAmount, 0)}
                          <div style={{fontSize: '0.8em', color: '#666', fontWeight: 'normal'}}>
                            (Escalation starts in {yearsUntilEscalation} year{yearsUntilEscalation > 1 ? 's' : ''})
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // No escalation yet (less than 1 year)
                    return (
                      <div className="info-card" style={{border: '2px solid #10b981', backgroundColor: '#f0fdf4'}}>
                        <div className="info-card-label">Current Rent</div>
                        <div className="info-card-value" style={{color: '#10b981', fontWeight: 'bold'}}>
                          {formatIndianCurrency(client.rentAmount, 0)}
                          <div style={{fontSize: '0.8em', color: '#666', fontWeight: 'normal'}}>
                            (No escalation yet)
                          </div>
                        </div>
                      </div>
                    );
                  }
                }
              })()}
            </div>
          </div>
          </>
          )}

          {activeTab === 'documents' && (
          <>
          {/* Client Documents Section */}
          <div className="info-section">
            <div className="info-section-header">
              <FileText size="1.2em" />
              <h3>Client Documents</h3>
            </div>
            {(() => {
              const d = client.documents || {};
              const docEntries = [
                { key: 'aadhar', label: 'Aadhar', path: d.aadhar },
                { key: 'pan', label: 'PAN', path: d.pan },
                { key: 'gst', label: 'GST', path: d.gst },
                { key: 'companyIncorporationCertificate', label: 'Company Incorporation Certificate', path: d.companyIncorporationCertificate },
                { key: 'directorsAadharPan', label: 'Directors Aadhar & PAN', path: d.directorsAadharPan },
                { key: 'gstCertificate', label: 'GST Certificate', path: d.gstCertificate },
                { key: 'gstrFiling', label: 'GSTR Filing', path: d.gstrFiling },
                { key: 'leaseDeed', label: 'Lease Deed', path: d.leaseDeed },
                { key: 'loi', label: 'LOI', path: d.loi },
                { key: 'termsheet', label: 'Termsheet', path: d.termsheet },
                { key: 'serviceAgreement', label: 'Service Agreement', path: d.serviceAgreement },
              ].filter((entry) => !!entry.path);
              if (docEntries.length === 0) {
                return <div style={{ color: '#666', padding: '8px 0' }}>No documents uploaded.</div>;
              }
              return (
                <div className="info-grid">
                  {docEntries.map(({ key, label, path }) => (
                    <div className="info-card" key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        <div className="info-card-label">{label}</div>
                        <div className="info-card-value">
                          <button type="button" onClick={() => openViewer(label, path)} className="link-button" style={{ color: '#1976d2', textDecoration: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                           <strong>{getFileName(path)}</strong>
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <a href={`${API_BASE}${path}`} target="_blank" rel="noopener noreferrer" title="Download" style={{ width: 36, height: 36, borderRadius: 12, background: '#eef2f7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#374151', textDecoration: 'none' }}>
                          <Download size={16} />
                        </a>
                        {currentUser?.role === 'admin' && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            title="Delete document"
                            onClick={async () => {
                              if (!window.confirm(`Remove ${label}?`)) return;
                              try {
                                const fd = new FormData();
                                fd.append(`clear_${key}`,'true');
                                await onUpdateClient(client._id || client.id, fd);
                                const updated = clients.find(c => c._id === clientId || c.id === clientId);
                                setClient(updated);
                              } catch (e) {
                                alert('Failed to remove document');
                              }
                            }}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          </>
          )}

          {activeTab === 'invoices' && (
            <div className="info-section">
              <div className="info-section-header">
                <FileText size="1.2em" />
                <h3>Invoices</h3>
              </div>
              {loadingInvoices ? (
                <div style={{ padding: 12, color: '#666' }}>Loading invoices...</div>
              ) : clientInvoices.length === 0 ? (
                <div style={{ padding: 12, color: '#666' }}>No invoices found for this client.</div>
              ) : (
                <div className="table-container">
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Invoice No</th>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Total</th>
                          <th>Received</th>
                          <th>TDS Deducted</th>
                          <th>Outstanding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientInvoices.map(inv => {
                          const total = Number(inv.totals?.total || 0);
                          const subtotal = Number(inv.totals?.subtotal || 0);
                          const tdsPercent = Number(inv.tdsPercent || 0);
                          const tdsAmount = tdsPercent > 0 ? subtotal * (tdsPercent / 100) : 0;
                          const received = Number(inv.receivedAmount || 0);
                          const outstanding = Math.max(0, total - received - tdsAmount);
                          return (
                            <tr key={inv._id || inv.id}>
                              <td>
                                <button
                                  type="button"
                                  className="invoice-number-link"
                                  onClick={() => setSelectedInvoice(inv)}
                                  title="Click to view invoice"
                                  style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                  <strong>{inv.invoiceType === 'credit_note' 
                                    ? (inv.creditNoteNumber || inv.invoiceNumber)
                                    : formatInvoiceNumber(inv.invoiceNumber, inv.financialYear, config)
                                  }</strong>
                                </button>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                  backgroundColor: inv.invoiceType === 'adhoc' ? '#ff6b35' : (inv.invoiceType === 'credit_note' ? '#2563eb' : '#4facfe'),
                                  color: 'white'
                                }}>
                                  {inv.invoiceType === 'adhoc' ? 'Adhoc' : inv.invoiceType === 'credit_note' ? 'Credit' : 'Rent'}
                                </span>
                              </td>
                              <td>{inv.invoiceDate ? format(new Date(inv.invoiceDate), 'dd/MM/yyyy') : ''}</td>
                              <td>{formatIndianCurrency(total)}</td>
                              <td>{received ? formatIndianCurrency(received) : ''}</td>
                              <td>
                                {tdsAmount ? formatIndianCurrency(tdsAmount) : ''}
                                {tdsPercent > 0 && (
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    TDS: {tdsPercent}%
                                  </div>
                                )}
                              </td>
                              <td>{outstanding ? formatIndianCurrency(outstanding) : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ledger' && (
            <div className="info-section">
              <div className="info-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size="1.2em" />
                  <h3>Ledger</h3>
                </div>
                <button className="btn btn-secondary" onClick={exportLedgerExcel}>Export Excel</button>
              </div>
              {loadingInvoices ? (
                <div style={{ padding: 12, color: '#666' }}>Loading...</div>
              ) : (
                (() => {
                  const rows = [];
                  let balance = 0;
                  clientInvoices.forEach(inv => {
                    const date = inv.invoiceDate ? new Date(inv.invoiceDate) : null;
                    const total = Number(inv.totals?.total || 0);
                    balance += total;
                    rows.push({ date, description: `Invoice ${inv.invoiceType === 'credit_note' 
                      ? (inv.creditNoteNumber || inv.invoiceNumber)
                      : formatInvoiceNumber(inv.invoiceNumber, inv.financialYear, config)
                    }`, debit: total, credit: 0, balance });
                    const received = Number(inv.receivedAmount || 0);
                    if (received > 0) {
                      balance -= received;
                      rows.push({ date: inv.receivedDate ? new Date(inv.receivedDate) : date, description: `Payment ${inv.utrNumber || ''}`.trim(), debit: 0, credit: received, balance });
                    }
                  });
                  if (rows.length === 0) return <div style={{ padding: 12, color: '#666' }}>No transactions found.</div>;
                  return (
                    <div className="table-container">
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Description</th>
                              <th>Debit</th>
                              <th>Credit</th>
                              <th>Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.sort((a,b) => (b.date?.getTime()||0) - (a.date?.getTime()||0)).map((r, idx) => (
                              <tr key={idx}>
                                <td>{r.date ? format(r.date, 'dd/MM/yyyy') : ''}</td>
                                <td>{r.description}</td>
                                <td>{r.debit ? formatIndianCurrency(r.debit) : ''}</td>
                                <td>{r.credit ? formatIndianCurrency(r.credit) : ''}</td>
                                <td>{formatIndianCurrency(r.balance)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>
      )}
      {selectedInvoice && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '10px', padding: 0, maxWidth: '900px', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="no-print" style={{ position:'sticky', top:0, left:0, right:0, background:'#fff', zIndex:5, padding:'10px 16px', borderBottom:'1px solid #eee', display:'flex', alignItems:'center', justifyContent:'space-between', borderTopLeftRadius:'10px', borderTopRightRadius:'10px' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {selectedInvoice.invoiceType === 'credit_note' 
                  ? (selectedInvoice.creditNoteNumber || selectedInvoice.invoiceNumber)
                  : formatInvoiceNumber(selectedInvoice.invoiceNumber, selectedInvoice.financialYear, config)
                }
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button className="btn btn-primary" onClick={handlePrint}>
                  <Printer style={{marginRight: 6}}/>Print Invoice
                </button>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }} onClick={() => setSelectedInvoice(null)} aria-label="Close"><X /></button>
              </div>
            </div>
            <div style={{ padding: '20px 30px 30px 30px' }}>
            {selectedInvoice.invoiceType === 'adhoc' ? (
              <AdhocInvoicePreview
                client={selectedInvoice.client}
                invoiceNumber={selectedInvoice.invoiceNumber}
                invoiceDate={selectedInvoice.invoiceDate}
                placeOfSupply={selectedInvoice.placeOfSupply}
                items={selectedInvoice.items}
                subtotal={selectedInvoice.totals.subtotal}
                cgst={selectedInvoice.totals.cgst}
                sgst={selectedInvoice.totals.sgst}
                total={selectedInvoice.totals.total}
                amountInWords={selectedInvoice.amountInWords}
                printRef={printRef}
                handlePrint={handlePrint}
                config={config}
                poNumber={selectedInvoice.poNumber}
                poDate={selectedInvoice.poDate}
                showShipTo={selectedInvoice.showShipTo}
                financialYear={selectedInvoice.financialYear}
                hideInlinePrintButton
              />
            ) : (
              <InvoicePreview
                client={selectedInvoice.client}
                invoiceNumber={selectedInvoice.invoiceNumber}
                invoiceDate={selectedInvoice.invoiceDate}
                placeOfSupply={selectedInvoice.placeOfSupply}
                items={selectedInvoice.items}
                subtotal={selectedInvoice.totals.subtotal}
                cgst={selectedInvoice.totals.cgst}
                sgst={selectedInvoice.totals.sgst}
                total={selectedInvoice.totals.total}
                amountInWords={selectedInvoice.amountInWords}
                printRef={printRef}
                handlePrint={handlePrint}
                config={config}
                poNumber={selectedInvoice.poNumber}
                poDate={selectedInvoice.poDate}
                showShipTo={selectedInvoice.showShipTo}
                financialYear={selectedInvoice.financialYear}
                isCreditNote={selectedInvoice.invoiceType === 'credit_note'}
                additionalInfo={(() => {
                  const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                    ? selectedInvoice.originalInvoice
                    : null;
                  return selectedInvoice.invoiceType === 'credit_note' && origInv
                    ? `This is a credit note made against invoice ${formatInvoiceNumber(origInv.invoiceNumber, origInv.financialYear || selectedInvoice.financialYear, config)}`
                    : undefined;
                })()}
                invoiceRefNumber={(() => {
                  const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                    ? selectedInvoice.originalInvoice
                    : null;
                  return origInv ? formatInvoiceNumber(origInv.invoiceNumber, origInv.financialYear || selectedInvoice.financialYear, config) : undefined;
                })()}
                originalInvoiceDate={(() => {
                  const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                    ? selectedInvoice.originalInvoice
                    : null;
                  return origInv ? origInv.invoiceDate : undefined;
                })()}
                hideQuoteNumber={(selectedInvoice.invoiceType || 'rent') === 'rent'}
              />
            )}
            </div>
          </div>
        </div>
      )}
      {viewerDoc && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: '95vw', height: '92vh', maxWidth: '1200px', padding: 0, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-right-header" style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>{viewerDoc.label}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isImageUrl(viewerDoc.url) && (
                  <>
                    <button className="btn btn-secondary" title="Zoom out" onClick={() => setImageScale(s => Math.max(0.25, s - 0.25))}><ZoomOut size={16} /></button>
                    <button className="btn btn-secondary" title="Zoom in" onClick={() => setImageScale(s => Math.min(5, s + 0.25))}><ZoomIn size={16} /></button>
                    <button className="btn btn-secondary" title="Rotate left" onClick={() => setImageRotate(r => r - 90)}><RotateCcw size={16} /></button>
                    <button className="btn btn-secondary" title="Rotate right" onClick={() => setImageRotate(r => r + 90)}><RotateCw size={16} /></button>
                    <button className="btn btn-secondary" title="Reset" onClick={() => { setImageScale(1); setImageRotate(0); }}><RefreshCcw size={16} /></button>
                  </>
                )}
                <a className="btn btn-secondary" href={viewerDoc.url} download={getFileName(viewerDoc.url)} title="Download">
                  <Download size={16} />
                </a>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }} onClick={closeViewer} aria-label="Close">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, height: '100%', background: '#f5f6fa' }}>
              {(() => {
                const url = viewerDoc.url;
                const isImage = isImageUrl(url);
                if (isImage) {
                  return (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', overflow: 'auto' }}>
                      <img src={url} alt={viewerDoc.label} style={{ maxWidth: '100%', maxHeight: '100%', transform: `scale(${imageScale}) rotate(${imageRotate}deg)`, transition: 'transform 0.15s ease', transformOrigin: 'center center' }} />
                    </div>
                  );
                }
                // Default: render in iframe (PDF or other types)
                return (
                  <iframe title={viewerDoc.label} src={url} style={{ width: '100%', height: '100%', border: 'none' }} />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetails;
